import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { toast } from '../hooks/use-toast';
import { Mail, Send } from 'lucide-react';
import { portfolioData } from '../mock';
import axios from 'axios';
import WhatsAppQR from './WhatsAppQR';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { contact } = portfolioData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send to backend
      await axios.post(`${API}/contact`, formData);
      
      toast({
        title: "Message Sent Successfully!",
        description: "Thank you for reaching out. I'll get back to you within 24 hours.",
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error Sending Message",
        description: "There was an issue sending your message. Please try again or email directly.",
        variant: "destructive"
      });
    }
  };

  const faqs = [
    {
      question: "What services do you offer?",
      answer: "I offer comprehensive digital marketing services including social media marketing, SEO, content creation, photography, website development, motion videos, logo design, and fashion modeling. Each service is tailored to meet your specific business needs."
    },
    {
      question: "How long does a typical project take?",
      answer: "Project timelines vary based on scope and complexity. A basic social media campaign might take 2-4 weeks, while comprehensive digital marketing strategies can span 2-3 months. I'll provide a detailed timeline during our initial consultation."
    },
    {
      question: "Do you work with international clients?",
      answer: "Absolutely! I work with clients globally and am experienced in managing remote collaborations across different time zones. All communication is handled professionally through video calls, email, and project management tools."
    },
    {
      question: "What is your pricing structure?",
      answer: "Pricing depends on project scope, deliverables, and timeline. I offer flexible packages ranging from starter plans for small businesses to premium enterprise solutions. Contact me for a custom quote tailored to your needs."
    },
    {
      question: "Can you help with ongoing social media management?",
      answer: "Yes! I offer monthly retainer packages for ongoing social media management, including content creation, posting schedules, community engagement, analytics reporting, and strategy optimization."
    }
  ];

  return (
    <section id="contact" className="py-32 bg-zinc-950 relative" aria-label="Contact section">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black opacity-50"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="text-gold-500 text-sm font-semibold tracking-widest uppercase mb-4 block">Contact</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6">
            Let's Work Together
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Ready to elevate your brand? Get in touch and let's create something amazing
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div>
            <Card className="bg-zinc-900/50 border-zinc-800 backdrop-blur-sm">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Your Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="bg-zinc-800/50 border-zinc-700 focus:border-gold-500 focus:ring-gold-500 text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="bg-zinc-800/50 border-zinc-700 focus:border-gold-500 focus:ring-gold-500 text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-2">
                      Subject
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Project Inquiry"
                      required
                      className="bg-zinc-800/50 border-zinc-700 focus:border-gold-500 focus:ring-gold-500 text-white placeholder:text-gray-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell me about your project..."
                      rows={5}
                      required
                      className="bg-zinc-800/50 border-zinc-700 focus:border-gold-500 focus:ring-gold-500 text-white placeholder:text-gray-500"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gold-500 hover:bg-gold-600 text-black py-6 text-lg font-semibold group"
                  >
                    Send Message
                    <Send className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Email Card */}
            <Card className="mt-6 bg-zinc-900/50 border-zinc-800 hover:border-gold-500/30 transition-all">
              <CardContent className="p-6">
                <a
                  href={`mailto:${contact.email}`}
                  className="flex items-center space-x-4 group"
                >
                  <div className="w-12 h-12 bg-gold-500/10 border border-gold-500/20 rounded-lg flex items-center justify-center group-hover:bg-gold-500/20 transition-colors">
                    <Mail className="w-6 h-6 text-gold-500" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-400">Email</div>
                    <div className="text-white font-medium group-hover:text-gold-500 transition-colors">
                      {contact.email}
                    </div>
                  </div>
                </a>
              </CardContent>
            </Card>

            {/* WhatsApp QR Code */}
            <div className="mt-6">
              <WhatsAppQR />
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <h3 className="font-display text-3xl font-bold text-white mb-8">Frequently Asked Questions</h3>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={faq.question}
                  value={`item-${index}`}
                  className="bg-zinc-900/50 border-zinc-800 rounded-lg px-6 backdrop-blur-sm"
                >
                  <AccordionTrigger className="text-left text-white hover:text-gold-500 font-medium py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 leading-relaxed pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
